<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { Checkbox } from 'primevue';
import { useListManageReportApi } from '../api/listManageReportApi';
import type { ReportResponse } from '../models/ReportResponse';
import { useToggleReportApi } from '../api/toggleReportApi';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { ReportType } from 'shared/reports/domain/reportType';

const listManageReportApi = useListManageReportApi();
const toggleReportApi = useToggleReportApi();
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const reports = ref<ReportResponse[]>([]);

const customerSpecificReportsList: ReportType[] = [];

onMounted(async () => {
  reports.value = await listManageReportApi.execute();
});

async function toggleReport(report: ReportResponse) {
  const booleanValue = Boolean(report.enabled);
  await toggleReportApi.execute({
    reportType: report.reportType,
    enabled: booleanValue,
  });
}

const defaultReports = computed(() => {
  return reports.value.filter(
    (report) => !customerSpecificReportsList.includes(report.reportType),
  );
});

const customerSpecificReports = computed(() => {
  return reports.value.filter((report) =>
    customerSpecificReportsList.includes(report.reportType),
  );
});

</script>

<template>
  <div>
    <div class="mt-3 mb-1">
      <label class="text-xl font-bold">{{ translations.systemSettings.page.reports.defaultReports }}</label>
    </div>

    <div v-for="report in defaultReports" :key="report.reportType" class="feature-flag">
      <div class="mt-2">
        <Checkbox v-model="report.enabled" @change="() => toggleReport(report)" class="me-2" size="small" binary />
        <label>{{ translations.reports.reportName(report.reportType) }}</label>
      </div>
    </div>

    <div v-if="customerSpecificReports.length > 0" class="mt-3 mb-1">
      <label class="text-xl font-bold">{{ translations.systemSettings.page.reports.customerSpecificReports }}</label>
    </div>

    <div v-for="report in customerSpecificReports" :key="report.reportType" class="feature-flag">
      <div class="mt-2">
        <Checkbox v-model="report.enabled" @change="() => toggleReport(report)" class="me-2" size="small" binary />
        <label>{{ translations.reports.reportName(report.reportType) }}</label>
      </div>
    </div>
  </div>
</template>
